import { useTranslation } from '@pancakeswap/localization'
import { Box, Card, ErrorState, Flex, Heading, Skeleton, Text } from '@pancakeswap/uikit'
import styled from 'styled-components'
import type { StatBucket, VersionBuckets } from 'server/multiswapRecord/types'
import { useMultiSwapStats, DexId, VersionParam } from 'state/infoHub/hooks'
import { timeAgo, usd } from './format'

const Head = styled(Flex)`
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-bottom: 12px;
`
const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`
const Stat = styled.div`
  min-width: 0;
  padding-left: 12px;
  border-left: 1px solid ${({ theme }) => theme.colors.cardBorder};
`
const Split = styled.details`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSubtle};
  summary {
    cursor: pointer;
    min-height: 32px;
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.primary};
      outline-offset: 2px;
      border-radius: 4px;
    }
  }
  table {
    width: 100%;
    margin-top: 6px;
    border-collapse: collapse;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.text};
  }
  th,
  td {
    text-align: right;
    padding: 6px 8px;
    border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
    white-space: nowrap;
  }
  th:first-child,
  td:first-child {
    text-align: left;
    padding-left: 0;
  }
  th {
    color: ${({ theme }) => theme.colors.textSubtle};
    font-weight: 600;
    border-top: 0;
  }
`

const fees = (b: StatBucket) => (b.feesUSD !== null ? usd(b.feesUSD) : b.fees.length ? b.fees.map((f) => `${f.symbol} ${f.amount}`).join(', ') : b.trades ? '—' : '$0.00')

const StatCell: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Stat>
    <Text fontSize="12px" color="textSubtle" bold>
      {label}
    </Text>
    <Text fontSize="18px" bold style={{ overflowWrap: 'anywhere', lineHeight: 1.25 }}>
      {value}
    </Text>
    {hint && (
      <Text fontSize="12px" color="textSubtle">
        {hint}
      </Text>
    )}
  </Stat>
)

/**
 * Set (a) of the brief: trades that went through our app, verified on-chain server-side (docs/MULTISWAP-ATTRIBUTION.md).
 * One compact card at the end of the overview, kept apart from the public pool statistics above it.
 */
export const ActivityBlock: React.FC<{ scope: 'total' | DexId; version: VersionParam }> = ({ scope, version }) => {
  const { t } = useTranslation()
  const { stats, error, isLoading, retry } = useMultiSwapStats()
  const scopeLabel = scope === 'total' ? t('all DEXes') : scope === 'cadinu' ? 'CADINUdex' : 'PancakeSwap'

  let body: React.ReactNode
  let meta: React.ReactNode = null
  if (isLoading && !stats) {
    body = (
      <Row>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height="44px" />
        ))}
      </Row>
    )
  } else if (error && !stats) {
    body = <ErrorState title={t('Activity statistics unavailable')} description={error.message} onRetry={retry} retryLabel={t('Retry')} py="8px" />
  } else if (stats) {
    const buckets: VersionBuckets = stats.buckets[scope]
    const bucket: StatBucket = version === 'all' ? buckets.all : buckets[version]
    const since = stats.recordingSince
    meta = (
      <Text fontSize="12px" color="textSubtle">
        {since ? `${t('Recording since')} ${new Date(since).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : t('Recording started with this release')}
        {' · '}
        {t('updated')} {timeAgo(stats.updatedAt)}
        {stats.pendingCount > 0 ? ` · ${stats.pendingCount} ${t('pending')}` : ''}
      </Text>
    )
    body =
      bucket.trades === 0 ? (
        <Text fontSize="14px" color="textSubtle" data-testid="activity-empty">
          {t('No confirmed trades in this scope yet. A trade appears after %n% confirmations and on-chain verification; nothing is back-filled.', { n: stats.confirmationsRequired })}
        </Text>
      ) : (
        <>
          <Row>
            <StatCell label={t('Volume (input side)')} value={usd(bucket.volumeUSD)} hint={bucket.unpricedTrades ? t('%n% trades without a USD price', { n: bucket.unpricedTrades }) : undefined} />
            <StatCell label={t('Trades')} value={bucket.trades.toLocaleString()} />
            <StatCell label={t('Unique wallets')} value={bucket.uniqueWallets.toLocaleString()} hint={version === 'all' ? t('union across versions') : undefined} />
            <StatCell label={t('CADINU fee collected')} value={fees(bucket)} hint={t('confirmed fee transfers only')} />
          </Row>
          {version === 'all' && (
            <Split>
              <summary>{t('By version')}</summary>
              <table>
                <thead>
                  <tr>
                    <th>{t('Version')}</th>
                    <th>{t('Trades')}</th>
                    <th>{t('Volume')}</th>
                    <th>{t('Wallets')}</th>
                    <th>{t('Fees')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(['v2', 'v3', 'mixed', 'stable'] as const)
                    .filter((k) => buckets[k] && (buckets[k]!.trades > 0 || k !== 'stable'))
                    .map((k) => {
                      const b = buckets[k]!
                      return (
                        <tr key={k}>
                          <td>{k === 'mixed' ? t('Mixed V2/V3') : k.toUpperCase()}</td>
                          <td>{b.trades}</td>
                          <td>{usd(b.volumeUSD)}</td>
                          <td>{b.uniqueWallets}</td>
                          <td>{fees(b)}</td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </Split>
          )}
        </>
      )
  }

  return (
    <Card data-testid="activity-block">
      <Box p={['16px', null, '20px 24px']}>
        <Head>
          <Box>
            <Heading scale="md" as="h3">
              {t('CADINU MultiSwap activity')}
              <Text as="span" fontSize="14px" color="textSubtle" ml="8px" style={{ fontWeight: 400 }}>
                {scopeLabel}
              </Text>
            </Heading>
            <Text fontSize="13px" color="textSubtle">
              {t('Trades sent from this app and verified on-chain; not the market volume of the pools above.')}
            </Text>
          </Box>
          {meta}
        </Head>
        {body}
      </Box>
    </Card>
  )
}

export default ActivityBlock
